import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import { useReactToPrint } from 'react-to-print';
import { configurationApi } from '@/services/configuration.api';
import { wizardApi } from '@/services/wizard.api';
import { LoadingState } from '@/components/common/LoadingState';
import { ErrorState } from '@/components/common/ErrorState';
import { SummarySheet } from '@/components/resume/SummarySheet';
import { formatPrice } from '@/utils/price.utils';
import { toTitleCase } from '@/utils/text.utils';
import type { ConfigurationMetadata, ConfigurationSummary } from '@/types';

/**
 * Riepilogo della configurazione (Fase C): scelte per fase + valore totale + stampa/scarica PDF.
 * Il PDF è client-side (react-to-print, come il legacy). Il flusso di conferma/firma (Box Sign)
 * è fuori scope in questa fase.
 */
export default function ConfigurationResumePage() {
  const { id } = useParams<{ id: string }>();
  const configId = Number(id);
  const navigate = useNavigate();

  const [summary, setSummary] = useState<ConfigurationSummary | null>(null);
  const [metadata, setMetadata] = useState<ConfigurationMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef: printRef });

  const load = (): void => {
    setIsLoading(true);
    setError(null);
    Promise.all([configurationApi.getSummary(configId), wizardApi.getMetadata(configId)])
      .then(([summaryData, metadataData]) => {
        setSummary(summaryData);
        setMetadata(metadataData);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Impossibile caricare il riepilogo della configurazione. Riprova.');
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (Number.isNaN(configId)) {
      setError('Configurazione non valida.');
      setIsLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId]);

  return (
    <Box sx={{ flex: 1, px: { xs: 2, md: 6 }, py: 5 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
          La tua configurazione
        </Typography>
        <Typography variant="h2">{metadata?.configurationName ?? '—'}</Typography>
        {metadata && (
          <Typography variant="body1" color="text.secondary">
            {toTitleCase(metadata.apartmentName)}
            {metadata.apartmentCode ? ` · ${metadata.apartmentCode}` : ''}
          </Typography>
        )}
      </Box>

      {isLoading && <LoadingState message="Caricamento riepilogo…" />}
      {error && <ErrorState message={error} onRetry={load} />}

      {!isLoading && !error && summary && (
        <Grid container spacing={3} alignItems="flex-start">
          {/* Colonna SX: fogli per-fase (area di stampa). */}
          <Grid item xs={12} md={8}>
            <Box ref={printRef}>
              {summary.phases.length === 0 ? (
                <Typography variant="body1" color="text.secondary">
                  Non hai ancora effettuato scelte per questa configurazione.
                </Typography>
              ) : (
                <Stack spacing={0}>
                  {summary.phases.map((phase) => (
                    <SummarySheet key={phase.phaseKey} phase={phase} />
                  ))}
                </Stack>
              )}
            </Box>
          </Grid>

          {/* Colonna DX: valore totale + azioni (sticky). */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
              <Typography variant="body2" sx={{ textTransform: 'uppercase' }} color="text.secondary">
                Il <strong>valore totale</strong> delle scelte da te effettuate è di
              </Typography>
              <Typography sx={{ mt: 1, fontWeight: 750, fontSize: 30, textTransform: 'uppercase', color: 'primary.main' }}>
                {formatPrice(summary.total)}
              </Typography>

              <Stack spacing={1.5} sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/configurations/${configId}`)}
                >
                  Modifica le tue scelte
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PrintIcon />}
                  onClick={() => handlePrint()}
                >
                  Stampa / Scarica PDF
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
